import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";
import ObtieneDatosCookies from "../obtieneDatosCookiesUsuario";
import { useNavigate} from 'react-router-dom';
const API = "https://wwwa.isepsantafe.net.ar/expedientes/getReporteIncumbenciasDisponibilidad";
const texto = (v) => String(v ?? "").trim();
const numero = (v) => v === null || v === undefined || v === "" ? null : Number(v);
const etiquetaMes = (mes) => {
  if (!mes) return "";
  const [anio, mesNumero] = mes.split("-").map(Number);
  return new Intl.DateTimeFormat("es-AR", { month: "short", year: "numeric", timeZone: "UTC" })
    .format(new Date(Date.UTC(anio, mesNumero - 1, 1)));
};
const fechaLocal = (valor) => valor ? String(valor).slice(0, 10).split("-").reverse().join("/") : "";
const escuelasFila = (f) => Array.isArray(f.escuelas) && f.escuelas.length ? f.escuelas : texto(f.escuela).split(/\s+\/\s+/).filter(Boolean);
const normalizar = (v) => texto(v).normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
const asignacionesInc = (f) => Array.isArray(f.asignaciones) ? f.asignaciones : [];
const cursosFila = (f) => [...new Set(asignacionesInc(f).map(a => texto(a.curso)).filter(Boolean))];
const divisionesFila = (f) => [...new Set(asignacionesInc(f).map(a => texto(a.division)).filter(Boolean))];
const limpiarNombreHoja = (nombre) => texto(nombre).replace(/[\\/?*\[\]:]/g, " ").slice(0, 31) || "Sin escuela";

export default function ReporteIncumbenciasDisponibilidad({ puedeGestionarIncumbencias = false }) {
  const [reporte, setReporte] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");
  const [escuela, setEscuela] = useState("");
  const [materia, setMateria] = useState("");
  const [tipo, setTipo] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [soloDisponibles, setSoloDisponibles] = useState(false);
  const [soloSinDocente, setSoloSinDocente] = useState(false);
  const [mesSeleccionado, setMesSeleccionado] = useState("");
  const [pagina, setPagina] = useState(1);
  const [tamPagina, setTamPagina] = useState(25);
  const [expandidas, setExpandidas] = useState({});
  const [soloConAsignacion, setSoloConAsignacion] = useState(false);

  const consultar = async () => {
    setCargando(true);
    setError("");
    try {
      const { data } = await axios.post(API, {}, { timeout: 120000 });
      if (!data || !Array.isArray(data.filas)) throw new Error("La API no devolvió el campo filas como arreglo.");
      setReporte(data);
      setMesSeleccionado(data.periodo?.meses?.[0] || "");
      setPagina(1);
      setExpandidas({});
    } catch (e) {
      setError(e.response?.data?.message || e.response?.data?.error || e.message || "No se pudo obtener el reporte.");
      setReporte(null);
    } finally {
      setCargando(false);
    }
  };
    const navitate = useNavigate()
  const volver = () => {
    navitate('/Docentes')
  }

  useEffect(() => { consultar(); }, []);
  // El permiso debe venir de una comprobación autorizada en la aplicación.
  // El backend también debe verificarlo: esta bandera solo controla la interfaz.
  const obtenerUsuario = () => ObtieneDatosCookies();

  const obtenerIncumbenciasActuales = async (dni) => {
    try {
      const { data } = await axios.post(
        `https://wwwa.isepsantafe.net.ar/expedientes/docentes/getIncumbenciasPorDni/${encodeURIComponent(dni)}`,
        {}
      );
      return (data.incumbencias || []).map(inc => Number(inc.id));
    } catch (err) {
      if (err.response?.status === 404) return [];
      throw err;
    }
  };

  const actualizarIncumbencia = async (dni, id, accion) => {
    if (!puedeGestionarIncumbencias || guardando) return;
    const usuario = obtenerUsuario();
    if (!usuario?.identificacion) {
      await Swal.fire("Usuario no identificado", "No se pudo obtener la identificación del usuario.", "error");
      return;
    }
    setGuardando(true);
    try {
      // Consultamos TODAS las incumbencias actuales, no solo las visibles en el reporte.
      const actuales = await obtenerIncumbenciasActuales(dni);
      const idNumero = Number(id);
      if (!Number.isInteger(idNumero)) throw new Error("ID de incumbencia inválido");
      const competencias = accion === "agregar"
        ? [...new Set([...actuales, idNumero])]
        : actuales.filter(x => x !== idNumero);
      const desactivar = accion === "quitar" ? [idNumero] : [];
      await axios.post(
        "https://wwwa.isepsantafe.net.ar/expedientes/docentes/actualizarIncumbenciasDocente",
        { docenteDni: String(dni), competencias, desactivar, usuarioID: usuario.identificacion }
      );
      await consultar();
      await Swal.fire("Actualización exitosa", "Las incumbencias fueron actualizadas.", "success");
    } catch (err) {
      await Swal.fire("No se pudo actualizar", err.response?.data?.message || err.message, "error");
    } finally {
      setGuardando(false);
    }
  };

  const confirmarQuitar = async (fila) => {
    if (!puedeGestionarIncumbencias || fila.sinDocente) return;
    const respuesta = await Swal.fire({
      title: "¿Quitar incumbencia?",
      text: `${fila.docente} (DNI ${fila.docenteDni}) dejará de estar habilitado para ${fila.materia}. No se eliminan sus asignaciones académicas.`,
      icon: "warning", showCancelButton: true,
      confirmButtonText: "Sí, quitar incumbencia", cancelButtonText: "Cancelar",
      confirmButtonColor: "#b42318"
    });
    if (respuesta.isConfirmed) await actualizarIncumbencia(fila.docenteDni, fila.incumbenciaId, "quitar");
  };

  const abrirAsignacion = async () => {
    if (!puedeGestionarIncumbencias || guardando) return;
    const docentes = [...new Map((reporte?.filas || [])
      .filter(f => f.docenteDni && !f.sinDocente)
      .map(f => [String(f.docenteDni), { dni: String(f.docenteDni), nombre: f.docente }])).values()]
      .sort((a, b) => a.nombre.localeCompare(b.nombre, "es"));
    const incumbencias = [...new Map((reporte?.filas || [])
      .filter(f => f.incumbenciaId != null)
      .map(f => [String(f.incumbenciaId), { id: Number(f.incumbenciaId), nombre: f.materia, escuela: f.escuela }])).values()]
      .sort((a, b) => a.nombre.localeCompare(b.nombre, "es"));
    if (!docentes.length || !incumbencias.length) {
      await Swal.fire("Sin datos", "No hay docentes o incumbencias disponibles en el reporte.", "info");
      return;
    }
    // DOM del modal de SweetAlert, no se interpolan nombres en HTML.
    const resultado = await Swal.fire({
      title: "Asignar incumbencia",
      html: `<div style="text-align:left"><label for="swal-docente">Docente</label><select id="swal-docente" class="swal2-select" style="width:100%;margin:6px 0 14px"></select><label for="swal-incumbencia">Incumbencia</label><select id="swal-incumbencia" class="swal2-select" style="width:100%;margin:6px 0"></select><small>Se mostrarán solamente incumbencias que el docente aún no tenga.</small></div>`,
      didOpen: async () => {
        const dSelect = document.getElementById("swal-docente");
        const iSelect = document.getElementById("swal-incumbencia");
        for (const d of docentes) dSelect.add(new Option(`${d.nombre} — ${d.dni}`, d.dni));
        const cargar = async () => {
          iSelect.replaceChildren(new Option("Consultando incumbencias...", ""));
          iSelect.disabled = true;
          try {
            const actuales = new Set(await obtenerIncumbenciasActuales(dSelect.value));
            iSelect.replaceChildren();
            for (const inc of incumbencias.filter(x => !actuales.has(x.id))) {
              iSelect.add(new Option(`${inc.nombre} — ${inc.escuela} (ID ${inc.id})`, String(inc.id)));
            }
            if (!iSelect.options.length) iSelect.add(new Option("No tiene incumbencias pendientes", ""));
          } catch {
            iSelect.replaceChildren(new Option("Error al consultar incumbencias", ""));
          } finally { iSelect.disabled = false; }
        };
        dSelect.addEventListener("change", cargar);
        await cargar();
      },
      showCancelButton: true, confirmButtonText: "Asignar", cancelButtonText: "Cancelar",
      preConfirm: () => {
        const dni = document.getElementById("swal-docente")?.value;
        const id = document.getElementById("swal-incumbencia")?.value;
        if (!dni || !id) { Swal.showValidationMessage("Seleccioná un docente y una incumbencia disponible."); return false; }
        return { dni, id: Number(id) };
      }
    });
    if (resultado.isConfirmed && resultado.value) {
      await actualizarIncumbencia(resultado.value.dni, resultado.value.id, "agregar");
    }
  };

  const filas = reporte?.filas || [];
  const meses = reporte?.periodo?.meses || [];
  const escuelas = useMemo(() => [...new Set(filas.flatMap(escuelasFila).filter(Boolean))].sort((a,b) => a.localeCompare(b,"es")), [filas]);
  const materias = useMemo(() => [...new Set(filas.filter(f => !escuela || escuelasFila(f).some(e => normalizar(e) === normalizar(escuela))).map(f => texto(f.materia)).filter(Boolean))].sort((a,b) => a.localeCompare(b,"es")), [filas, escuela]);
  const tipos = useMemo(() => [...new Set(filas.map(f => texto(f.docenteTipo)).filter(Boolean))].sort(), [filas]);
  const obtenerMes = (fila, mes) => (fila.disponibilidad || []).find(d => d.mes === mes);

  const filtradas = useMemo(() => filas.filter(f => {
    if (escuela && !escuelasFila(f).some(e => normalizar(e) === normalizar(escuela))) return false;
    if (materia && texto(f.materia) !== materia) return false;
    if (tipo && texto(f.docenteTipo) !== tipo) return false;
    if (soloSinDocente && !f.sinDocente) return false;
    if (soloConAsignacion && !asignacionesInc(f).length) return false;
    if (soloDisponibles && !(numero(obtenerMes(f, mesSeleccionado)?.horasDisponibles) > 0)) return false;
    const q = busqueda.toLocaleLowerCase("es").trim();
    return !q || [f.escuela, f.materia, f.docente, f.docenteDni, f.curso, f.sede, f.division, ...asignacionesInc(f).flatMap(a => [a.curso, a.division, a.sede, a.escuela])].some(v => texto(v).toLocaleLowerCase("es").includes(q));
  }), [filas, escuela, materia, tipo, soloSinDocente, soloDisponibles, mesSeleccionado, busqueda, soloConAsignacion]);

  useEffect(() => { setPagina(1); }, [escuela, materia, tipo, soloSinDocente, soloDisponibles, mesSeleccionado, busqueda, tamPagina, soloConAsignacion]);
  const totalPaginas = Math.max(1, Math.ceil(filtradas.length / tamPagina));
  const visibles = filtradas.slice((pagina - 1) * tamPagina, pagina * tamPagina);
  const resumen = useMemo(() => ({
    registros: filtradas.length,
    docentes: new Set(filtradas.map(f => f.docenteDni).filter(Boolean)).size,
    sinDocente: new Set(filtradas.filter(f => f.sinDocente).map(f => `${f.escuela}|${f.materiaId}`)).size,
    disponibles: new Set(filtradas.filter(f => numero(obtenerMes(f, mesSeleccionado)?.horasDisponibles) > 0).map(f => f.docenteDni).filter(Boolean)).size,
  }), [filtradas, mesSeleccionado]);

  const crearHoja = (registros) => {
    const columnas = ["Escuela", "Sede", "Curso", "Divisiones", "Asignaciones de incumbencia", "ID materia", "Materia", "Inicio materia", "Fin materia", "Categoría", "DNI", "Docente", "Tipo", "Escuela habilitada", "Máximo mensual", "Sin docente"];
    meses.forEach(m => columnas.push(`${etiquetaMes(m)} - Disponibles`, `${etiquetaMes(m)} - Ocupadas`, `${etiquetaMes(m)} - Exceso`));
    const matriz = [columnas, ...registros.map(f => {
      const base = [f.escuela || "", f.sede || "", cursosFila(f).join(" / ") || f.curso || "", divisionesFila(f).join(" / ") || f.division || "", asignacionesInc(f).length, f.materiaId ?? "", f.materia || "", fechaLocal(f.materiaDesde), fechaLocal(f.materiaHasta), f.categoria || "", f.docenteDni || "", f.docente || "", f.docenteTipo || "", f.escuelaHabilitado || "", f.limiteMensual ?? "", f.sinDocente ? "SÍ" : "NO"];
      meses.forEach(m => {
        const d = obtenerMes(f, m);
        base.push(d?.horasDisponibles ?? "", d?.horasOcupadas ?? "", d?.exceso ?? "");
      });
      return base;
    })];
    const hoja = XLSX.utils.aoa_to_sheet(matriz);
    hoja["!cols"] = columnas.map((_, i) => ({ wch: i === 7 || i === 12 ? 35 : i < 16 ? 18 : 19 }));
    hoja["!autofilter"] = { ref: hoja["!ref"] };
    return hoja;
  };

  const exportar = () => {
    if (!filtradas.length) return;
    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, crearHoja(filtradas), "Reporte general");
    const nombresUsados = new Set(["Reporte general", "Asignaciones", "Cursos y divisiones", "Resumen"]);
    for (const nombre of [...new Set(filtradas.flatMap(f => escuelasFila(f).length ? escuelasFila(f) : ["Sin escuela"]))]) {
      let nombreHoja = limpiarNombreHoja(nombre);
      let i = 2;
      while (nombresUsados.has(nombreHoja)) nombreHoja = `${limpiarNombreHoja(nombre).slice(0, 27)} ${i++}`;
      nombresUsados.add(nombreHoja);
      XLSX.utils.book_append_sheet(libro, crearHoja(filtradas.filter(f => escuelasFila(f).length ? escuelasFila(f).some(e => normalizar(e) === normalizar(nombre)) : nombre === "Sin escuela")), nombreHoja);
    }
    // Detalle académico: una fila por asignación real de la incumbencia.
    const detalleAcademico = [];
    const vistosAcademicos = new Set();
    for (const f of filtradas) {
      for (const a of asignacionesInc(f)) {
        const clave = `${f.incumbenciaId}|${f.docenteDni}|${a.id}`;
        if (vistosAcademicos.has(clave)) continue;
        vistosAcademicos.add(clave);
        detalleAcademico.push({
          "Incumbencia ID": f.incumbenciaId, "Materia": f.materia,
          "Escuelas habilitadas": f.escuela, "DNI": f.docenteDni,
          "Docente": f.docente, "Escuela asignada": a.escuela || "",
          "Curso": a.curso || "", "División": a.division ?? "",
          "Sede": a.sede || "", "ID asignación": a.id,
          "Horas": a.horasAsignadas ?? "", "Desde": fechaLocal(a.fechaInicio),
          "Hasta": fechaLocal(a.fechaFinalizacion), "Condición": a.condicion || ""
        });
      }
    }
    XLSX.utils.book_append_sheet(libro, XLSX.utils.json_to_sheet(detalleAcademico.length ? detalleAcademico : [{ "Detalle": "Sin asignaciones para las incumbencias filtradas" }]), "Cursos y divisiones");
    // Carga global: incluye otras materias; no repetir por incumbencia.
    const detalle = [];
    const idsVistos = new Set();
    for (const f of filtradas) {
      for (const d of f.disponibilidad || []) {
        for (const a of d.asignaciones || []) {
          const clave = `${f.docenteDni}|${d.mes}|${a.id}`;
          if (idsVistos.has(clave)) continue;
          idsVistos.add(clave);
          detalle.push({ "DNI": f.docenteDni, "Docente": f.docente, "Mes": d.mes,
            "ID asignación": a.id, "Materia": a.materia || "", "Escuela": a.escuela || "",
            "Curso": a.curso || "", "División": a.division ?? "", "Sede": a.sede || "",
            "Horas": a.horas, "Desde": fechaLocal(a.desde), "Hasta": fechaLocal(a.hasta) });
        }
      }
    }
    XLSX.utils.book_append_sheet(libro, XLSX.utils.json_to_sheet(detalle.length ? detalle : [{ "Detalle": "Sin asignaciones en el período" }]), "Asignaciones");
    XLSX.utils.book_append_sheet(libro, XLSX.utils.aoa_to_sheet([
      ["REPORTE DE INCUMBENCIAS Y DISPONIBILIDAD"],
      ["Desde", reporte.periodo?.desde || ""], ["Hasta", reporte.periodo?.hasta || ""],
      ["Registros filtrados", resumen.registros], ["Docentes únicos", resumen.docentes],
      ["Materias sin docente", resumen.sinDocente], ["Docentes con disponibilidad en el mes seleccionado", resumen.disponibles],
      ["Mes seleccionado", mesSeleccionado],
      ["Nota", "Las horas se calculan en el backend; este Excel reproduce los valores informados por la API."]
    ]), "Resumen");
    XLSX.writeFile(libro, `Incumbencias_Disponibilidad_${new Date().toISOString().slice(0,10)}.xlsx`, { compression: true });
  };

  const estilo = { border: "1px solid #dce3ec", borderRadius: 8, padding: "9px 11px", background: "white", minWidth: 140, fontSize: 13 };
  return (
    <div style={{ padding: 22, background: "#f4f7fb", minHeight: "100vh", color: "#17253b", fontFamily: "Arial, sans-serif" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <div><h2 style={{ margin: "0 0 6px" }}>Incumbencias y disponibilidad docente</h2><div style={{ color: "#65758b", fontSize: 13 }}>Proyección mensual de 18 meses · SIGEDI</div></div>
        <div style={{ display: "flex", gap: 8 }}>
          {puedeGestionarIncumbencias && <button onClick={abrirAsignacion} disabled={guardando || cargando} style={{ ...estilo, background: "#1c5a9a", color: "white", cursor: "pointer", border: "none", fontWeight: 700 }}>+ Asignar incumbencia</button>}
          <button onClick={consultar} disabled={cargando} style={{ ...estilo, background: "#fff", cursor: "pointer" }}>{cargando ? "Consultando..." : "Actualizar datos"}</button>
         
          <button onClick={exportar} disabled={!filtradas.length || cargando} style={{ ...estilo, background: "#176f4a", color: "white", cursor: "pointer", border: "none", fontWeight: 700 }}>Exportar Excel ({filtradas.length})</button>
        <button className="btn btn-warning" onClick={volver}>Volver</button>
        </div>
      </div>
      {error && <div role="alert" style={{ padding: 14, background: "#fff0f0", color: "#a51f1f", borderRadius: 8, marginBottom: 15 }}>{error}</div>}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 18 }}>
        {[["Registros", resumen.registros], ["Docentes únicos", resumen.docentes], ["Materias sin docente", resumen.sinDocente], ["Docentes con horas libres", resumen.disponibles]].map(([label, valor]) => <div key={label} style={{ background: "white", padding: 16, borderRadius: 10, border: "1px solid #e1e7ef" }}><div style={{ fontSize: 12, color: "#65758b" }}>{label}</div><div style={{ fontSize: 25, fontWeight: 700, marginTop: 5 }}>{valor}</div></div>)}
      </div>
      <div style={{ background: "white", border: "1px solid #e1e7ef", padding: 16, borderRadius: 10, marginBottom: 16 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
          <input style={{ ...estilo, flex: "2 1 220px" }} placeholder="Buscar materia, docente, DNI, curso..." value={busqueda} onChange={e => setBusqueda(e.target.value)} />
          <select style={estilo} value={escuela} onChange={e => { setEscuela(e.target.value); setMateria(""); }}><option value="">Todas las escuelas</option>{escuelas.map(v => <option key={v}>{v}</option>)}</select>
          <select style={estilo} value={materia} onChange={e => setMateria(e.target.value)}><option value="">Todas las materias</option>{materias.map(v => <option key={v}>{v}</option>)}</select>
          <select style={estilo} value={tipo} onChange={e => setTipo(e.target.value)}><option value="">Todos los tipos</option>{tipos.map(v => <option key={v}>{v}</option>)}</select>
          <select style={estilo} value={mesSeleccionado} onChange={e => setMesSeleccionado(e.target.value)}>{meses.map(m => <option value={m} key={m}>{etiquetaMes(m)}</option>)}</select>
          <label style={{ fontSize: 13 }}><input type="checkbox" checked={soloDisponibles} onChange={e => setSoloDisponibles(e.target.checked)} /> Con horas disponibles</label>
          <label style={{ fontSize: 13 }}><input type="checkbox" checked={soloConAsignacion} onChange={e => setSoloConAsignacion(e.target.checked)} /> Solo con asignación en incumbencia</label>
          <label style={{ fontSize: 13 }}><input type="checkbox" checked={soloSinDocente} onChange={e => setSoloSinDocente(e.target.checked)} /> Sin docente habilitado</label>
          <button style={estilo} onClick={() => { setEscuela(""); setMateria(""); setTipo(""); setBusqueda(""); setSoloDisponibles(false); setSoloSinDocente(false); setSoloConAsignacion(false); }}>Limpiar filtros</button>
        </div>
      </div>
      <div style={{ background: "white", border: "1px solid #e1e7ef", borderRadius: 10, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}><table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, whiteSpace: "nowrap" }}>
          <thead><tr style={{ background: "#eaf0f7", textAlign: "left" }}>{["Detalle", "Escuela", "Materia", "Curso / División", "Docente", "DNI", "Tipo", "Máximo", "Ocupadas", "Disponibles", "Exceso"].map(t => <th key={t} style={{ padding: 12, borderBottom: "1px solid #dce3ec" }}>{t}</th>)}</tr></thead>
          <tbody>{visibles.map((f, i) => {
            const d = obtenerMes(f, mesSeleccionado);
            const asignaciones = asignacionesInc(f);
            const clave = `${f.incumbenciaId ?? f.materiaId}|${f.docenteDni || "sin"}`;
            const abierta = !!expandidas[clave];
            return <React.Fragment key={clave}>
              <tr style={{ background: i % 2 ? "#f9fbfe" : "white" }}>
                <td style={{ padding: 11 }}><button type="button" disabled={!asignaciones.length} onClick={() => setExpandidas(prev => ({ ...prev, [clave]: !prev[clave] }))} style={{ cursor: asignaciones.length ? "pointer" : "default", border: "1px solid #dce3ec", background: "white", borderRadius: 6, padding: "5px 9px" }} title={asignaciones.length ? "Ver cursos y divisiones" : "Sin asignaciones para esta incumbencia"}>{asignaciones.length ? (abierta ? "−" : "+") : "—"}</button></td>
                <td style={{ padding: 11 }}>{f.escuela}</td>
                <td style={{ padding: 11 }}>{f.materia}</td>
                <td style={{ padding: 11, maxWidth: 330, whiteSpace: "normal" }}>{asignaciones.length ? <><strong>{cursosFila(f).join(" / ") || f.curso || "Curso no informado"}</strong><div style={{ color: "#65758b", marginTop: 3 }}>{divisionesFila(f).length ? `Divisiones: ${divisionesFila(f).join(", ")} · ` : ""}{asignaciones.length} asignación(es)</div></> : <span style={{ color: "#7b8795" }}>Sin asignación en esta incumbencia</span>}</td>
                <td style={{ padding: 11, fontWeight: 600, color: f.sinDocente ? "#b42318" : "inherit" }}>{puedeGestionarIncumbencias && !f.sinDocente ? <button type="button" disabled={guardando} onClick={() => confirmarQuitar(f)} title="Quitar esta incumbencia al docente" style={{ border: "none", background: "transparent", padding: 0, color: "#155b9c", fontWeight: 700, cursor: "pointer", textDecoration: "underline", textAlign: "left" }}>{f.docente}</button> : f.docente}</td>
                <td style={{ padding: 11 }}>{f.docenteDni || "—"}</td>
                <td style={{ padding: 11 }}>{f.docenteTipo || "—"}</td>
                <td style={{ padding: 11 }}>{f.limiteMensual ?? "—"}</td>
                <td style={{ padding: 11 }}>{d?.horasOcupadas ?? "—"}</td>
                <td style={{ padding: 11, color: numero(d?.horasDisponibles) > 0 ? "#167347" : "#a34120", fontWeight: 700 }}>{d?.horasDisponibles ?? "—"}</td>
                <td style={{ padding: 11, color: numero(d?.exceso) > 0 ? "#c32222" : "inherit" }}>{d?.exceso ?? "—"}</td>
              </tr>
              {abierta && <tr style={{ background: "#eef5fb" }}><td colSpan={11} style={{ padding: "14px 20px", whiteSpace: "normal" }}><strong>Asignaciones activas de esta incumbencia</strong><div style={{ overflowX: "auto", marginTop: 10 }}><table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}><thead><tr>{["Escuela", "Curso", "División", "Sede", "Horas", "Inicio", "Finalización", "Condición"].map(h => <th key={h} style={{ textAlign: "left", padding: 8, borderBottom: "1px solid #ccd8e5" }}>{h}</th>)}</tr></thead><tbody>{asignaciones.map((a, j) => <tr key={`${a.id}-${j}`}><td style={{ padding: 8 }}>{a.escuela || "—"}</td><td style={{ padding: 8 }}>{a.curso || "—"}</td><td style={{ padding: 8 }}>{a.division ?? "—"}</td><td style={{ padding: 8 }}>{a.sede || "—"}</td><td style={{ padding: 8 }}>{a.horasAsignadas ?? "—"}</td><td style={{ padding: 8 }}>{fechaLocal(a.fechaInicio)}</td><td style={{ padding: 8 }}>{fechaLocal(a.fechaFinalizacion)}</td><td style={{ padding: 8 }}>{a.condicion || "—"}</td></tr>)}</tbody></table></div><div style={{ fontSize: 12, color: "#65758b", marginTop: 10 }}>Las horas ocupadas de la tabla principal corresponden a todas las materias del docente, no solamente a esta incumbencia.</div></td></tr>}
            </React.Fragment>;
          })}</tbody>
        </table></div>
        {!visibles.length && <div style={{ padding: 28, textAlign: "center", color: "#65758b" }}>{cargando ? "Cargando datos..." : "No hay resultados para los filtros seleccionados."}</div>}
        <div style={{ padding: 12, display: "flex", justifyContent: "space-between", flexWrap: "wrap", alignItems: "center", gap: 10, borderTop: "1px solid #e1e7ef" }}>
          <span style={{ fontSize: 13, color: "#65758b" }}>Página {pagina} de {totalPaginas} · {filtradas.length} registros</span>
          <div style={{ display: "flex", gap: 8 }}><select style={estilo} value={tamPagina} onChange={e => setTamPagina(Number(e.target.value))}>{[25, 50, 100, 250].map(n => <option key={n} value={n}>{n} por página</option>)}</select><button style={estilo} disabled={pagina <= 1} onClick={() => setPagina(p => p - 1)}>Anterior</button><button style={estilo} disabled={pagina >= totalPaginas} onClick={() => setPagina(p => p + 1)}>Siguiente</button></div>
        </div>
      </div>
      <p style={{ color: "#65758b", fontSize: 12, marginTop: 14 }}>El Excel exporta todos los registros filtrados, no solo la página visible. Los valores de disponibilidad provienen del cálculo del backend. Una misma persona puede aparecer en varias incumbencias, pero se cuenta una sola vez como docente único. Si no aparece un curso, puede no existir una asignación para esa incumbencia o puede faltar la vinculación en el backend.</p>
    </div>
  );
}
